/**
 * The free delivery allowance of the month, read off `usage-periods` — what the home card's gauge
 * and the two delivery banners draw (`docs/plans/billing-ui.md` § Wave 3, items 5.1 and 5.3).
 *
 * **The allowance is the period's own `included_quantity`**, frozen when the period opened, never
 * the price book's allowance: a tariff changed mid-month does not rewrite what that month was
 * promised. Quantities are bytes, the meter's integral unit; the price past the allowance is the
 * organization's delivery rate (`prices.ts`), per decimal gigabyte.
 */

import { BYTES_PER_GIGABYTE, CATALOG_PRICES, type PriceBook } from './prices'

/** The share of the allowance at which the first banner shows. */
export const DELIVERY_WARNING_RATIO = 0.8

/**
 * Where a wallet stands with the month's free delivery — the terabyte that comes with paying
 * (`docs/plans/anti-abuse-rust.md` decision 11), read off the wallet's `free_allowance_from`:
 *
 * - `never` — it has never paid: delivery is charged from its first gigabyte, and the allowance
 *   comes with the first month that begins after a first top-up;
 * - `from` — it paid during the running month, which is still charged from its first gigabyte:
 *   the allowance comes with `from`, the first of the next month;
 * - `running` — the running month carries it, as its period's `included_quantity` says.
 */
export type Franchise = { kind: 'never' } | { kind: 'from'; from: string } | { kind: 'running' }

/**
 * The franchise on `today` (`YYYY-MM-DD`), from `free_allowance_from`: `null` is a wallet that has
 * never paid, a day the first of the month its allowance starts with. A server that does not say
 * (`undefined`) is read as it was before the field existed: the running month carries it.
 */
export function franchiseOf(freeAllowanceFrom: string | null | undefined, today: string): Franchise {
  if (freeAllowanceFrom === null) return { kind: 'never' }
  if (freeAllowanceFrom === undefined) return { kind: 'running' }
  const monthStart = `${today.slice(0, 7)}-01`
  return freeAllowanceFrom > monthStart ? { kind: 'from', from: freeAllowanceFrom } : { kind: 'running' }
}

/** What a period says, of what the allowance needs. */
export interface DeliveryPeriod {
  period_start: string
  period_end: string
  quantity: number
  included_quantity: number
  billable_quantity: number
}

/** The month's delivery, as a screen reads it. */
export interface DeliveryState {
  /** `YYYY-MM-DD`: the period's first day, which also keys a dismissal to its month. */
  periodStart: string
  usedBytes: number
  includedBytes: number
  /** What is past the allowance so far. */
  billableBytes: number
  /** Used over included, unbounded above; `0` for a period with no allowance. */
  ratio: number
}

/** Local `YYYY-MM-DD`. */
export function isoDay(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/**
 * The period running on `today` (`YYYY-MM-DD`, inclusive both ends), or `null` — before the first
 * measurement of the month opens one, nothing has been delivered that a screen should mention.
 */
export function currentDelivery(
  periods: readonly DeliveryPeriod[] | null | undefined,
  today: string,
): DeliveryState | null {
  const period = (periods ?? []).find(
    (entry) => entry.period_start <= today && today <= entry.period_end,
  )
  if (!period) return null
  return {
    periodStart: period.period_start,
    usedBytes: period.quantity,
    includedBytes: period.included_quantity,
    billableBytes: period.billable_quantity,
    ratio: period.included_quantity > 0 ? period.quantity / period.included_quantity : 0,
  }
}

/** Delivery's price past the allowance, in cents per gigabyte — the organization's. */
export function deliveryRateCents(book: PriceBook = CATALOG_PRICES): number {
  return book.prices.bandwidth.cents
}

/**
 * The monthly allowance a month opened today would carry, in bytes — the organization's own where
 * one was negotiated. Only a period not yet opened reads it: a period's allowance is its own.
 */
export function deliveryAllowanceBytes(book: PriceBook = CATALOG_PRICES): number {
  return book.prices.bandwidth.freeBytes ?? 0
}

/** What the bytes past the allowance cost so far, excluding tax, in whole cents. */
export function deliveryBilledCents(billableBytes: number, book: PriceBook = CATALOG_PRICES): number {
  return Math.round((Math.max(0, billableBytes) / BYTES_PER_GIGABYTE) * deliveryRateCents(book))
}

/** Which of the two delivery banners the month calls for, if one. */
export type DeliveryLevel = 'near' | 'exceeded' | null

export function deliveryLevel(state: DeliveryState | null): DeliveryLevel {
  if (!state || state.includedBytes <= 0) return null
  if (state.billableBytes > 0 || state.usedBytes > state.includedBytes) return 'exceeded'
  return state.ratio >= DELIVERY_WARNING_RATIO ? 'near' : null
}
